import type { Maybe } from '@adornis/base/utilTypes';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { DateTime } from 'luxon';
@Entity()
export class Draft extends MongoEntity {
  static override _class = 'Draft';
  static override _collectionName = 'drafts';

  static override get allFields() {
    return {
      _id: 1,
      _class: 1,
      assignedDesigner: 1,
      assignedReviewer: 1,
      updatedAt: 1,
      name: 1,
      description: 1,
      statusKey: 1,
      createdAt: 1,
      createdBy: 1,
      referenceID: 1,
      status: {
        name: 1,
      },
      entity: selectionSet(() => MongoEntity, 10),
    } as any;
  }

  @validate(nonOptional())
  @Field(type => String)
  referenceID!: string;

  @validate(nonOptional())
  @Field(type => MongoEntity)
  entity!: MongoEntity;

  @validate(nonOptional())
  @Field(type => DateTime, { default: v => v ?? DateTime.now() })
  createdAt!: DateTime;

  @validate(nonOptional())
  @Field(type => String, { default: v => v ?? 'invalid' })
  createdBy!: string;

  @Field(type => DateTime)
  updatedAt?: Maybe<DateTime>;

  @Field(type => String)
  name?: string;

  @Field(type => String)
  description?: string;

  @Field(type => String)
  assignedDesigner?: Maybe<string>;

  @Field(type => String)
  assignedReviewer?: Maybe<string>;

  @Field(type => DraftStatus)
  status?: DraftStatus;

  get statusKey(): keyof typeof DraftStatusKeys {
    return this.status?.name ?? 'IN_PROGRESS';
  }

  constructor(data: PartialEntityData<Draft>) {
    super(data);
  }

  static override async beforeUpdate(oldEntity: PartialEntityData<Draft>, update: Draft): Promise<void> {
    update.updatedAt = DateTime.now();
  }
}

export enum DraftStatusKeys {
  IN_PROGRESS = 'In Bearbeitung',
  IN_REVIEW = 'In Korrektur',
  IN_REVISION = 'In Überarbeitung',
  SCHEDULED_DEPLOY = 'Geplante Veröffentlichung',
}

export class DraftStatusFactory {
  static create(name: keyof typeof DraftStatusKeys): DraftStatus {
    switch (name) {
      case 'IN_REVIEW':
        return new DraftStatusInReview({ name });
      case 'IN_REVISION':
        return new DraftStatusInRevision({ name });
      case 'SCHEDULED_DEPLOY':
        return new DraftStatusScheduledDeploy({ name });
      default:
        return new DraftStatusInProgress({ name });
    }
  }
}

@Entity()
export abstract class DraftStatus extends AdornisEntity {
  static override _class = 'DraftStatus';

  @validate(nonOptional())
  @Field(type => String)
  name!: keyof typeof DraftStatusKeys;
}

@Entity()
export class DraftStatusInProgress extends DraftStatus {
  static override _class = 'DraftStatusInProgress';
}

@Entity()
export class DraftStatusInReview extends DraftStatus {
  static override _class = 'DraftStatusInReview';
}

@Entity()
export class DraftStatusInRevision extends DraftStatus {
  static override _class = 'DraftStatusInRevision';
}

@Entity()
export class DraftStatusScheduledDeploy extends DraftStatus {
  static override _class = 'DraftStatusScheduledDeploy';

  @validate(nonOptional())
  @Field(type => DateTime)
  deployAt!: DateTime;

  @validate(nonOptional({ allowFalsy: true }))
  @Field(type => Boolean)
  publish!: boolean;

  constructor(data: PartialEntityData<DraftStatusScheduledDeploy>) {
    super(data);
  }
}

@Entity()
export class CampusDraft extends Draft {
  static override _class = 'CampusDraft';
}

@Entity()
export class MarketingDraft extends Draft {
  static override _class = 'MarketingDraft';
}
